import type { Answers, ChatbotExtractedTags, ChatbotIntent, ChatbotPropertyType, FlowIntent } from './types'

/**
 * Chatbot answers → the structured fields the CRM reads in
 * `initializeDealForm()` to prefill the "Operación sugerida" card.
 *
 * The prose in `message` is built from these very same answers, so sending
 * them already structured saves the CRM an LLM call to re-extract them.
 * Only the keys the CRM accepts are emitted; anything we didn't ask in the
 * conversation is omitted rather than guessed.
 */

/** Flow option values → the Spanish `property_type` vocabulary the CRM accepts. */
const PROPERTY_TYPE_TAG: Record<string, ChatbotPropertyType> = {
  piso: 'piso',
  chalet: 'chalet',
  local: 'local',
  garaje: 'plaza_de_parking',
  otro: 'otro',
}

/**
 * Budget option values → the MAXIMUM of the bracket, digits only, which is what
 * the CRM reads into `budget_max`. The open-ended top bracket ("more than …")
 * has no maximum, so it sends nothing rather than passing its floor off as a
 * ceiling — that would understate what the visitor can afford.
 */
const PRESUPUESTO_COMPRA_MAX: Record<string, string> = {
  lt_200000: '200000',
  '200000_400000': '400000',
  '400000_600000': '600000',
  '600000_800000': '800000',
}

const PRESUPUESTO_ALQUILER_MAX: Record<string, string> = {
  lt_1000: '1000',
  '1000_1500': '1500',
  '1500_2000': '2000',
  '2000_4000': '4000',
}

/** Province option values → the label the CRM stores in `location`. */
const LOCATION_TAG: Record<string, string> = {
  valencia: 'Valencia',
  castellon: 'Castellón',
  alicante: 'Alicante',
}

function pick<T>(map: Record<string, T>, key: unknown): T | undefined {
  return typeof key === 'string' ? map[key] : undefined
}

function str(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const s = value.trim()
  return s.length > 0 ? s : undefined
}

/**
 * `intent` for the inquiry. rent/buy are what fix the operation type in the
 * CRM. The branches that aren't a buyer or a tenant send nothing rather than
 * the nearest-looking value.
 */
export function buildIntent(flowIntent: FlowIntent): ChatbotIntent[] | undefined {
  switch (flowIntent) {
    case 'comprar':
      return ['buy']
    case 'alquilar':
      return ['rent']
    // An owner wants to sell or rent out what's theirs, and no value on the
    // CRM's list means that today. Sending one would be a guess, and with
    // `type: 'valuation'` nothing reads it anyway.
    case 'propietario':
      return undefined
    case 'agente':
    case 'admin':
      return ['get_info']
    case 'publicidad':
      return undefined
  }
}

/**
 * `extracted_tags` for the inquiry. `source` is added server-side so it can
 * never be dropped or overridden by the client.
 */
export function buildExtractedTags(
  flowIntent: FlowIntent,
  answers: Answers
): Omit<ChatbotExtractedTags, 'source'> {
  const tags: Omit<ChatbotExtractedTags, 'source'> = {}

  switch (flowIntent) {
    case 'comprar':
    case 'alquilar': {
      // Buyers who gave a reference skip the province/type/budget questions.
      const propertyType = pick(PROPERTY_TYPE_TAG, answers.tipo_propiedad)
      if (propertyType) tags.property_type = propertyType
      const location = pick(LOCATION_TAG, answers.provincia)
      if (location) tags.location = location
      const budget =
        flowIntent === 'alquilar'
          ? pick(PRESUPUESTO_ALQUILER_MAX, answers.presupuesto_alquiler)
          : pick(PRESUPUESTO_COMPRA_MAX, answers.presupuesto)
      if (budget) tags.budget = budget
      break
    }
    case 'propietario': {
      const propertyType = pick(PROPERTY_TYPE_TAG, answers.property_type)
      if (propertyType) tags.property_type = propertyType
      const location = str(answers.property_location)
      if (location) tags.location = location
      break
    }
    case 'agente':
    case 'admin':
    case 'publicidad':
      break
  }

  return tags
}
